import { Icon, IconAndText, ModalDrawer } from '@blueprint/ui';
import {
    BinOutlineSmall,
    EmailOutlineSmall,
    ShareiosSmall
} from '@blueprint/icons';
import { Icons } from '@blueprint/ui/dist/Icon/icons';
import React from 'react';

import {
    SavedListingInfo,
    SavedListingPrice,
    SavedListingTitle,
    SavedListingWrapper,
    SavedListingAddress,
    SavedListingTransports,
    SavedListingDetails,
    SavedListingCardFooter,
    SavedListingBranchImage,
    SavedListingFooterInfoBlock,
    SavedListingPublishedInfo,
    SavedListingActions,
    StyledButton,
    SavedListingLink,
    SavedListingTransportList,
    BinOutlineColored,
    ShareSmallColored,
    ShareiosSmallColored,
    MobileOptionsButton,
    StyledLink,
    EmailOutlineSmallColored
} from './SavedListingCard.styles';

import { MOBILE_BREAKPOINT } from 'listing-details/constants';
import useIsWidthLessThanBreakpoint from 'shared/hooks/app/useIsWidthLessThanBreakpoint';
import { ListingFeatureList } from 'shared/components/listing-data/listing-features';
import { ListingImage } from 'shared/components/listing-data/listing-image';
import { PublicationStatus } from 'graphql.generated';
import {
    trackContactAgentClick,
    trackSavedListingClick
} from './SavedListingCard.tracking';
import { formatDate } from 'shared/helpers';
import {
    getContactLink,
    getNearestTransport
} from './SavedListingCard.helpers';
import ListingSharing from '../ListingSharing/ListingSharing';
import {
    ShareLinkButton,
    ShareList,
    ShareListItem
} from '../ListingSharing/ListingSharing.styles';
import { getMobileOS } from 'shared/helpers/getMobileOS';
import { useSavedListingCardModals } from './SavedListingCard.hooks';
import { LifecycleTag } from 'shared/components/listing-data/lifecycle-tag';
import { Saved_ListingFragment } from 'listing-details/queries/SavedListing.fragment.generated';

const ListingDetails = ({
    listing,
    detailsUri,
    isMobile
}: {
    listing: Saved_ListingFragment;
    detailsUri: string;
    isMobile: boolean;
}) => {
    const { title, pricing, displayAddress, publicationStatus } = listing;

    const savedListingPrice = pricing?.label ? (
        <SavedListingPrice>{pricing.label}</SavedListingPrice>
    ) : null;

    const detailsBlock = isMobile ? (
        <>
            <LifecycleTag listing={listing} />
            <SavedListingAddress>{displayAddress}</SavedListingAddress>
            {savedListingPrice}
        </>
    ) : (
        <>
            <LifecycleTag listing={listing} />
            <SavedListingTitle>{title}</SavedListingTitle>
            {savedListingPrice}
            <SavedListingAddress>{displayAddress}</SavedListingAddress>
        </>
    );

    return publicationStatus === PublicationStatus.Expired ? (
        <div>{detailsBlock}</div>
    ) : (
        // we cannot use Next/Link here for the following reason:
        // commercial and overseas properties exist at for-sale/details/{commercial | overseas}/<id>
        // but this app will capture that under the catch-all for-sale/*
        // by using an <a> tag instead, we will hit nginx and be re-directed first rather than being routed through the app.
        <SavedListingLink href={detailsUri} onClick={trackSavedListingClick}>
            {detailsBlock}
        </SavedListingLink>
    );
};

export type SavedListingCardProps = {
    listing: Saved_ListingFragment;
    deleteListing: () => void;
};

export const SavedListingCard = ({
    listing,
    deleteListing
}: SavedListingCardProps) => {
    const [isMobile] = useIsWidthLessThanBreakpoint([MOBILE_BREAKPOINT]);

    const {
        listingId,
        branch,
        featurePreview,
        listingUris,
        imagePreview,
        numberOfImages,
        publicationStatus,
        publishedOn,
        title,
        transports
    } = listing;

    const detailsUri = listingUris?.detail || '';
    const contactLink = getContactLink(listing);

    const {
        menuModalIsShowing,
        openMenuModal,
        closeMenuModal,
        shareModalIsShowing,
        openShareModal,
        closeShareModal
    } = useSavedListingCardModals();

    const shareModal = (
        <ModalDrawer isShowing={shareModalIsShowing} close={closeShareModal}>
            <ListingSharing
                data={{
                    title,
                    listingId,
                    listingUris
                }}
                category="User Account"
            />
        </ModalDrawer>
    );

    const mobileMenuModal = isMobile ? (
        <ModalDrawer isShowing={menuModalIsShowing} close={closeMenuModal}>
            <ShareList>
                {publicationStatus === PublicationStatus.Expired ? null : (
                    <>
                        <ShareListItem>
                            <ShareLinkButton
                                onClick={openShareModal}
                                data-testid={`mobile-menu-share-button-${listingId}`}
                            >
                                {getMobileOS(navigator.userAgent) ===
                                'android' ? (
                                    <ShareSmallColored />
                                ) : (
                                    <ShareiosSmallColored />
                                )}
                                <span>Share</span>
                            </ShareLinkButton>
                        </ShareListItem>
                        <ShareListItem>
                            <ShareLinkButton
                                href={contactLink}
                                onClick={() =>
                                    trackContactAgentClick(listingId)
                                }
                            >
                                <EmailOutlineSmallColored />
                                <span>Contact agent</span>
                            </ShareLinkButton>
                        </ShareListItem>
                    </>
                )}
                <ShareListItem>
                    <ShareLinkButton
                        data-testid={`delete-button-${listingId}`}
                        onClick={deleteListing}
                    >
                        <BinOutlineColored />
                        <span>Remove from saved</span>
                    </ShareLinkButton>
                </ShareListItem>
            </ShareList>
        </ModalDrawer>
    ) : null;

    const mobileMenuButton = isMobile ? (
        <MobileOptionsButton
            variant="tertiary"
            iconOnly
            onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.preventDefault();
                openMenuModal();
            }}
            data-testid="mobile-menu"
        >
            <Icon iconId="menuMore" />
        </MobileOptionsButton>
    ) : null;

    const cardFooter = isMobile ? null : (
        <>
            <SavedListingCardFooter>
                <SavedListingFooterInfoBlock>
                    {branch?.logoUrl ? (
                        <SavedListingBranchImage
                            src={branch.logoUrl}
                            alt={`Marketed by ${branch.name}`}
                        />
                    ) : null}
                    {publishedOn ? (
                        <SavedListingPublishedInfo>
                            Listed on{' '}
                            <time>{formatDate(publishedOn, 'dd/MM/yyyy')}</time>
                        </SavedListingPublishedInfo>
                    ) : null}
                </SavedListingFooterInfoBlock>
                <SavedListingActions>
                    {publicationStatus === PublicationStatus.Expired ? null : (
                        <>
                            <StyledLink
                                href={contactLink}
                                variant="tertiary"
                                onClick={() =>
                                    trackContactAgentClick(listingId)
                                }
                            >
                                <EmailOutlineSmall />
                                Contact
                            </StyledLink>
                            <StyledButton
                                variant="tertiary"
                                onClick={openShareModal}
                                data-testid={`share-button-${listingId}`}
                            >
                                <ShareiosSmall />
                                Share
                            </StyledButton>
                        </>
                    )}
                    <StyledButton
                        variant="tertiary"
                        onClick={deleteListing}
                        data-testid={`delete-button-${listingId}`}
                    >
                        <BinOutlineSmall />
                        Remove
                    </StyledButton>
                </SavedListingActions>
            </SavedListingCardFooter>
        </>
    );

    const nearestTransport = isMobile ? null : getNearestTransport(transports);
    const transportSection = nearestTransport ? (
        <>
            <SavedListingTransportList>
                <SavedListingTransports
                    key={`${nearestTransport.poiType}-${nearestTransport.title}`}
                >
                    <IconAndText
                        isInline
                        iconId={nearestTransport.poiType as Icons}
                        content={`${nearestTransport.distanceInMiles} miles from ${nearestTransport.title}`}
                        contentColor="grey-steel"
                        iconColor="grey-steel"
                        size="small"
                    />
                </SavedListingTransports>
            </SavedListingTransportList>
        </>
    ) : null;

    return (
        <>
            <SavedListingWrapper data-testid="saved-listing-card">
                <SavedListingDetails isCompact={isMobile}>
                    <ListingImage
                        image={imagePreview}
                        sizes={isMobile ? '7.5rem' : '20rem'}
                        count={isMobile ? null : numberOfImages}
                    />
                    <SavedListingInfo>
                        <ListingDetails
                            listing={listing}
                            detailsUri={detailsUri}
                            isMobile={isMobile}
                        />
                        <ListingFeatureList features={featurePreview} />
                        {transportSection}
                    </SavedListingInfo>
                </SavedListingDetails>
                {mobileMenuButton}
                {cardFooter}
            </SavedListingWrapper>
            {mobileMenuModal}
            {shareModal}
        </>
    );
};
